import type {
  ApiResponse,
  FinancialObligationStatus,
  OmieBillingSlipIntegrationState,
} from '@bcube/shared';
import { api } from '@/lib/api';

export interface PublicProposalItem {
  productName: string;
  productDescription?: string;
  negotiationType: string;
  quantity: number;
  unitPriceCashInCents: number;
  unitPriceInstallmentInCents: number;
  subtotalCashInCents: number;
  subtotalInstallmentInCents: number;
  subtotalMonthlyInCents: number;
  discountPercent: number;
}

export interface PublicProposalTotals {
  totalCashInCents: number;
  totalInstallmentInCents: number;
  totalMonthlyInCents: number;
  totalItems: number;
}

export interface PublicProposalPayment {
  paymentType: 'CASH' | 'INSTALLMENTS';
  installments: number;
  dueDay: number;
}

export interface PublicProposalAddress {
  street: string;
  number: string | null;
  complement: string | null;
  district: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface PublicProposalData {
  proposalNumber: number;
  clientName: string;
  contactName: string;
  sellerName: string;
  sentDate: string | null;
  validityDate: string | null;
  isExpired: boolean;
  status: string;
  otpRequired: boolean;
  items: PublicProposalItem[];
  totals: PublicProposalTotals;
  payment: PublicProposalPayment | null;
  deliveryAddress: PublicProposalAddress | null;
  deliveryMode?: 'CLIENT' | 'BWS';
  pickupLocationName?: string | null;
  customerType: 'PF' | 'PJ';
  immediatePayment: {
    required: boolean;
    amountInCents: number;
    method: 'PIX' | 'BOLETO' | null;
  };
}

export type PublicFinancialObligationStatus = FinancialObligationStatus;
export interface PublicPaymentData {
  obligation: {
    id: string;
    method: 'PIX' | 'BOLETO';
    amountInCents: number;
    status: PublicFinancialObligationStatus;
  };
  pix: {
    key: string;
    keyType: string;
    beneficiaryName: string;
    institution?: string | null;
  } | null;
  billingSlip: {
    state: OmieBillingSlipIntegrationState;
    data: { link?: string | null; barcode?: string | null; dueDate?: string | null } | null;
  } | null;
}

export type PublicContractSignatureStatus = 'not_sent' | 'sending' | 'waiting' | 'signed' | 'error';
export interface PublicContractSignature {
  contractId: string;
  contractVersionId: string;
  type: string;
  version: number;
  status: PublicContractSignatureStatus;
  signerName: string | null;
  signerEmailMasked: string | null;
  sentAt: string | null;
  signedAt: string | null;
}
export interface PublicContractSignatures {
  contracts: PublicContractSignature[];
  allSigned: boolean;
}

export const publicProposalApi = {
  findContractSignatures: async (token: string): Promise<PublicContractSignatures> => {
    const { data } = await api.get<ApiResponse<PublicContractSignatures>>(
      `/commercial/proposals/public/${token}/contracts/signature`,
    );
    return data.data;
  },
  sendContractSignature: async (
    token: string,
    contractVersionId: string,
  ): Promise<PublicContractSignatures> => {
    const { data } = await api.post<ApiResponse<PublicContractSignatures>>(
      `/commercial/proposals/public/${token}/contracts/${contractVersionId}/signature`,
      undefined,
      { timeout: 45_000 },
    );
    return data.data;
  },
  resendContractSignature: async (
    token: string,
    contractVersionId: string,
  ): Promise<PublicContractSignatures> => {
    const { data } = await api.post<ApiResponse<PublicContractSignatures>>(
      `/commercial/proposals/public/${token}/contracts/${contractVersionId}/signature/resend`,
    );
    return data.data;
  },
  findOne: async (token: string): Promise<PublicProposalData> => {
    const { data } = await api.get<ApiResponse<PublicProposalData>>(
      `/commercial/proposals/public/${token}`,
    );
    return data.data;
  },

  sendOtp: async (token: string): Promise<{ destination: string }> => {
    const response = await api.post<ApiResponse<{ destination: string }>>(
      `/commercial/proposals/public/${token}/otp/send`,
    );
    return response.data.data;
  },

  verifyOtp: async (token: string, code: string): Promise<{ verified: boolean }> => {
    const { data } = await api.post<ApiResponse<{ verified: boolean }>>(
      `/commercial/proposals/public/${token}/otp/verify`,
      { code },
    );
    return data.data;
  },

  refuse: async (token: string, justification: string): Promise<{ refused: boolean }> => {
    const { data } = await api.post<ApiResponse<{ refused: boolean }>>(
      `/commercial/proposals/public/${token}/refuse`,
      { justification },
    );
    return data.data;
  },

  accept: async (
    token: string,
    declarationAccepted: true,
  ): Promise<{ accepted: boolean; status: string }> => {
    const { data } = await api.post<ApiResponse<{ accepted: boolean; status: string }>>(
      `/commercial/proposals/public/${token}/accept`,
      { declarationAccepted },
    );
    return data.data;
  },

  findPayment: async (token: string): Promise<PublicPaymentData> => {
    const { data } = await api.get<ApiResponse<PublicPaymentData>>(
      `/commercial/proposals/public/${token}/payment`,
    );
    return data.data;
  },
  createReceiptUpload: async (token: string, input: { originalName: string; mimeType: string }) => {
    const { data } = await api.post<ApiResponse<{ storageKey: string; uploadUrl: string }>>(
      `/commercial/proposals/public/${token}/payment/receipt-upload`,
      input,
    );
    return data.data;
  },
  confirmReceiptUpload: async (
    token: string,
    input: { storageKey: string; originalName: string; idempotencyKey: string },
  ) => {
    const { data } = await api.post<ApiResponse<unknown>>(
      `/commercial/proposals/public/${token}/payment/receipt-confirm`,
      input,
    );
    return data.data;
  },

  track: async (token: string, action: 'LINK_VIEWED' | 'DETAILS_VIEWED'): Promise<void> => {
    await api.post(`/commercial/proposals/public/${token}/track`, { action });
  },
};
